import { ExpandMore } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { TextButton } from '@retail/components';
import { useProductsTFunction } from '@retail/products/i18n';
import { useDisclosure } from '@shared/hooks';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

interface Props {
  name: string;
  value: string | number | undefined | ReactNode;
  gridColumns: string;
  expandable?: boolean;
}

export const ProductInformationRow = ({ name, value, gridColumns, expandable }: Props) => {
  const t = useProductsTFunction();
  const { isOpen: isExpanded, onToggle: toggleExpanded } = useDisclosure();
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const checkClamping = () => {
    const el = textRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
  };

  useLayoutEffect(() => {
    if (expandable) checkClamping();
  });

  return (
    <Stack display="grid" gridTemplateColumns={gridColumns} alignItems="center">
      <Typography variant="body2">{name}</Typography>
      <Box display="flex" flexDirection="column">
        <Typography
          ref={textRef}
          variant="subtitle2"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: isExpanded && expandable ? 'unset' : 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            boxShadow: expandable && !isExpanded && isClamped ? '0 1px 2px -2px gray' : 'unset',
          }}
        >
          {value ?? '-'}
        </Typography>
        {expandable && (isClamped || isExpanded) && (
          <TextButton
            size="small"
            onClick={() => toggleExpanded()}
            sx={{ alignSelf: 'flex-end' }}
            endIcon={
              <ExpandMore
                sx={{
                  transition: 'transform 0.2s ease-in-out',
                  transform: isExpanded ? 'rotate(180deg)' : 0,
                }}
              />
            }
          >
            <Typography variant="body3">
              {isExpanded ? t('products.hide') : t('products.expand')}
            </Typography>
          </TextButton>
        )}
      </Box>
    </Stack>
  );
};
